// AB#1751 — Calendar v1 C5 quick-book hooks (FE).
// Two surfaces:
//   useAvailabilitySlotsQuery  — slot search (BE PR #913 GET endpoint)
//   useBookAppointmentMutation — appointment create (BE POST endpoint)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import type {
    CalendarAvailabilityParams,
    CalendarAvailabilityResponse,
    BookAppointmentRequest,
    BookAppointmentResponse,
} from '../types/api-responses';

/**
 * Fetch slot candidates. 15-second `staleTime` — slots can be invalidated
 * by another owner / SignalR (C7). Disabled until branchId + serviceId +
 * from are all set so the picker doesn't fire on every keystroke.
 */
export const useAvailabilitySlotsQuery = (params: Partial<CalendarAvailabilityParams>) => {
    const enabled =
        typeof params.branchId === 'number' && params.branchId > 0
        && typeof params.serviceId === 'number' && params.serviceId > 0
        && typeof params.from === 'string' && params.from.length > 0;

    return useQuery<CalendarAvailabilityResponse>({
        queryKey: queryKeys.calendar.availability({
            branchId: params.branchId ?? 0,
            serviceId: params.serviceId ?? 0,
            from: params.from ?? '',
            around: params.around ?? null,
            staffId: params.staffId ?? null,
            staffGender: params.staffGender ?? null,
        }),
        queryFn: () => apiService.getCalendarAvailability({
            branchId: params.branchId as number,
            serviceId: params.serviceId as number,
            from: params.from as string,
            around: params.around ?? null,
            staffId: params.staffId ?? null,
            staffGender: params.staffGender ?? null,
            take: params.take ?? 15,
        }),
        enabled,
        staleTime: 15_000,
    });
};

/** Stable GUID per call; the caller stores it in component state. */
export function generateIdempotencyKey(): string {
    // crypto.randomUUID is available in all modern evergreen browsers and
    // node 14.17+, which is the floor for our CRA build. No polyfill needed.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Hyper-defensive fallback for environments without crypto.randomUUID
    // (e.g. very old test runners). RFC4122-shape but not cryptographically
    // strong — acceptable for an idempotency key.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
        const r = (Math.random() * 16) | 0;
        return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/**
 * Mutation hook for the booking POST. The caller passes the (body,
 * idempotencyKey) pair — the key MUST stay stable across retries within
 * the same logical booking, so the caller generates it once per modal
 * open and reuses it on every Book click.
 *
 * On success: invalidates the availability namespace (so the slot list
 * refreshes) and the calendar grid namespace from C3 FE (so the day
 * view repaints with the new card). The calendar key may not exist if
 * C3 hasn't merged yet — invalidate is a no-op in that case.
 */
export const useBookAppointmentMutation = () => {
    const qc = useQueryClient();
    return useMutation<
        BookAppointmentResponse,
        Error,
        { body: BookAppointmentRequest; idempotencyKey: string }
    >({
        mutationFn: ({ body, idempotencyKey }) =>
            apiService.bookAppointment(body, idempotencyKey),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.calendar.all });
        },
    });
};
